import { useEffect, useState } from 'react';

// Pages mark their largest image with fetchpriority="high" (home hero, the
// shop's first row, the product photo). Extras wait until those are in, so
// on a slow connection they never share the line with the largest paint.
const PRIORITY_POLL_MS = 250;
export const PRIORITY_MAX_WAIT_MS = 8000;

const priorityImagesPending = () => {
  if (typeof document === 'undefined') return false;
  return Array.from(document.querySelectorAll('img[fetchpriority="high"]')).some((img) => !img.complete);
};

/**
 * Renders its children only once the page has loaded, `delayMs` more have
 * passed (the SPA's `load` fires before the page's data and images arrive),
 * the page's high-priority images have finished (at most
 * PRIORITY_MAX_WAIT_MS) and the browser is idle (requestIdleCallback, at
 * most `timeout` ms later).
 * For floating extras (campaign bar, e-mail pop-up, concierge, install
 * prompt, WhatsApp and back-to-top buttons, cookie banner) so their code and
 * API calls stay out of the first paint and the largest image's download.
 */
const IdleMount = ({ children, delayMs = 1500, timeout = 4000 }) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let idleId = null;
    let timer = null;
    let waited = 0;
    const show = () => setReady(true);
    const whenIdle = () => {
      timer = null;
      if (typeof window.requestIdleCallback === 'function') idleId = window.requestIdleCallback(show, { timeout });
      else show();
    };
    const afterPriorityImages = () => {
      timer = null;
      if (priorityImagesPending() && waited < PRIORITY_MAX_WAIT_MS) {
        waited += PRIORITY_POLL_MS;
        timer = setTimeout(afterPriorityImages, PRIORITY_POLL_MS);
        return;
      }
      whenIdle();
    };
    const schedule = () => { timer = setTimeout(afterPriorityImages, delayMs); };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
    return () => {
      window.removeEventListener('load', schedule);
      if (idleId !== null && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idleId);
      if (timer !== null) clearTimeout(timer);
    };
  }, [delayMs, timeout]);

  return ready ? children : null;
};

export default IdleMount;
